import React, { useContext, useEffect, useState } from 'react';

import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { GridRowParams, GridValueFormatterParams } from '@mui/x-data-grid';

import CustomDataGrid from '../../components/data-grid';
import Screen from '../../components/screen';
import { AppContext } from '../../contexts/app-context';
import GlobalStyles from '../../global-styles';
import {
  checkUserAuthenticated, getSubscriptionList, goToRoutePage, setWindowTitle
} from '../../utils/helper';
import {
  Box, Breadcrumbs, Button, FormControl, Grid, InputLabel, LinearProgress, Link, Select,
  TextField, Typography
} from '../../utils/material-ui-core';

const Subscription = () => {
  const pageStyles = LocalPageStyles();
  const globalStyles = GlobalStyles();

  const subscriptionSearchCriteriaStorageName = "SubscriptionSearchCriteria";

  const [startUp, setStartUp] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [showAllFilterValues, setShowAllFilterValues] = useState<boolean>(false);
  const [clientCompanyKey, setClientCompanyKey] = useState<string>("");
  const [programKey, setProgramKey] = useState<string>("");
  const [countryCode, setCountryCode] = useState<string>("");
  const [states, setStates] = useState<State[]>([]);
  const [stateCode, setStateCode] = useState<string>("");
  const [status, setStatus] = useState<number>(-1);
  const [searchText, setSearchText] = useState<string>("");

  const { subscriptions, setSubscriptions, isUserAuthenticated, clientCompanies, programs, referenceLookups, showAlert } = useContext(AppContext);

  const columns = [
    {
      field: "clientAccountNumber",
      headerName: "Account #",
      width: 300,
      hide: true,
    },
    { field: "accountName", headerName: "Account Name", width: 300 },
    {
      field: "clientCompanyName",
      headerName: "Client Company Name",
      width: 300,
    },
    { field: "programName", headerName: "Program Name", width: 300 },
    { field: "contactFirstName", headerName: "Contact First Name", width: 300 },
    { field: "contactLastName", headerName: "Contact Last Name", width: 300 },
    {
      field: "contactEmail",
      headerName: "Contact Email",
      width: 300,
      hide: true,
    },
    { field: "state", headerName: "State", width: 130 },
    { field: "status", headerName: "Status", width: 130 },
    {
      field: "statusDate",
      headerName: "Status Date",
      width: 200,
      type: "dateTime",
    },
    {
      field: "locked",
      headerName: "Locked",
      width: 130,
      valueFormatter: (params: GridValueFormatterParams) => convertBoolenStringToString(params.value as string),
    },
    {
      field: "expiredCount",
      headerName: "Expired Count",
      width: 180,
      type: "number",
    },
    {
      field: "replacementKit",
      headerName: "Replacement Kit",
      width: 180,
      hide: true,
      valueFormatter: (params: GridValueFormatterParams) => convertBoolenStringToString(params.value as string),
    },
    {
      field: "kitNo",
      headerName: "Kit #",
      width: 120,
      type: "number",
      hide: true,
    },
  ];

  const convertBoolenStringToString = (val: string) => {
    if (val === "true") {
      return "Yes";
    }
    return "No";
  };

  const handleDataGridClick = (params: GridRowParams, event: any) => { };

  const getData = () => {
    const rows: any = [];
    subscriptions.map((subscription, index) => rows.push({ id: index, ...subscription }));
    return rows;
  };

  const search = async () => {
    setLoading(true);
    saveFilterCriteria();
    const apiResponse = await getSubscriptionList(clientCompanyKey, programKey, stateCode, status, searchText);
    if (apiResponse.success) {
      const data = apiResponse.data;
      setSubscriptions(data.subscriptions as Subscription[]);
      getData();
    } else {
      showAlert({ message: apiResponse.errorMessage, severity: "error" });
      return;
    }
    setLoading(false);
  };

  const searchFilterCriteriaReset = () => {
    setClientCompanyKey("");
    setProgramKey("");
    setCountryCode("");
    setStateCode("");
    countryChanged("");
    setStatus(-1);
    setSearchText("");
    searchCriteriaChanged();
    localStorage.removeItem(subscriptionSearchCriteriaStorageName);
  };

  const searchCriteriaChanged = () => {
    setSubscriptions([]);
  };

  const saveFilterCriteria = () => {
    const filterInput = {
      clientCompanyKey,
      programKey,
      countryCode,
      stateCode,
      searchText,
    };
    localStorage.setItem(subscriptionSearchCriteriaStorageName, JSON.stringify(filterInput));
  };

  const countryChanged = (countryCode: string) => {
    if (countryCode !== "" && referenceLookups.countries.find((country) => country.code === countryCode)?.code !== null) {
      setStateCode("");
      setStates(referenceLookups.countries.filter((country) => country.code === countryCode)[0].states);
    } else {
      setStates([]);
    }
  };

  useEffect(() => {
    setWindowTitle("Subscription");
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const initializePage = async () => {
      checkUserAuthenticated(isUserAuthenticated);
      if (isUserAuthenticated) {
        setLoading(false);
      }

      if (!startUp) {
        try {
          const searchCriteriaString = localStorage.getItem(subscriptionSearchCriteriaStorageName);
          if (searchCriteriaString !== null) {
            const searchCriteria = JSON.parse(searchCriteriaString);
            setClientCompanyKey(searchCriteria.clientCompanyKey);
            setProgramKey(searchCriteria.programKey);
            setCountryCode(searchCriteria.countryCode);
            countryChanged(searchCriteria.countryCode);
            setStateCode(searchCriteria.stateCode);
            setSearchText(searchCriteria.searchText);
          }
        } catch (e) { }

        setStartUp(true);
      }
    };
    initializePage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUserAuthenticated, subscriptions]);

  return (
    <React.Fragment>
      <Screen>
        <Box className={pageStyles.root}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Box className={globalStyles.bottomSpacer}>
                <Breadcrumbs aria-label="breadcrumb">
                  <Link color="inherit" onClick={() => goToRoutePage("home")} className={globalStyles.link}>
                    Home
                  </Link>
                  <Typography color="textPrimary">Subscription</Typography>
                </Breadcrumbs>
              </Box>
            </Grid>
          </Grid>

          <Grid container spacing={3}>
            {showAllFilterValues ? (
              <>
                <Grid item xs={12} sm={6} md={4} lg={4} xl={3}>
                  <FormControl className={globalStyles.formControl} fullWidth={true}>
                    <InputLabel shrink={true}>Client Company</InputLabel>
                    <Select
                      native
                      labelId="client-company-key-label"
                      id="clientCompanyKey"
                      name="clientCompanyKey"
                      value={clientCompanyKey}
                      onChange={(e) => {
                        if (e !== null) {
                          setClientCompanyKey(e.target.value as string);
                        }
                        searchCriteriaChanged();
                      }}
                      label="Client Company"
                    >
                      <option value="">Any</option>
                      {clientCompanies.map((clientCompany, index) => (
                        <option value={clientCompany.clientCompanyKey} key={index}>
                          {clientCompany.name}
                        </option>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={4} xl={3}>
                  <FormControl className={globalStyles.formControl} fullWidth={true}>
                    <InputLabel shrink={true}>Program</InputLabel>
                    <Select
                      native
                      labelId="program-key-label"
                      id="programKey"
                      name="programKey"
                      value={programKey}
                      onChange={(e) => {
                        if (e !== null) {
                          setProgramKey(e.target.value as string);
                        }
                        searchCriteriaChanged();
                      }}
                      label="Program"
                    >
                      <option value="">Any</option>
                      {programs.map((program, index) => (
                        <option value={program.programKey} key={index}>
                          {program.programName}
                        </option>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={4} xl={2}>
                  <FormControl className={globalStyles.formControl} fullWidth={true}>
                    <InputLabel shrink={true}>Status</InputLabel>
                    <Select
                      native
                      labelId="status-label"
                      id="status"
                      name="status"
                      value={status}
                      onChange={(e) => {
                        if (e !== null) {
                          setStatus(e.target.value as number);
                        }
                        searchCriteriaChanged();
                      }}
                      label="Status"
                    >
                      <option value="-1">Any</option>
                      <option value="0">Expired</option>
                      <option value="2">Ordered</option>
                      <option value="1">Active</option>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={4} xl={2}>
                  <FormControl className={globalStyles.formControl} fullWidth={true}>
                    <InputLabel shrink={true}>Country</InputLabel>
                    <Select
                      native
                      labelId="country-Code-label"
                      id="countryCode"
                      name="countryCode"
                      value={countryCode}
                      onChange={(e) => {
                        if (e !== null) {
                          setCountryCode(e.target.value as string);
                          countryChanged(e.target.value as string);
                        }
                        searchCriteriaChanged();
                      }}
                      label="Country"
                    >
                      <option value="">Any</option>
                      {referenceLookups.countries.map((country, index) => (
                        <option value={country.code} key={index}>
                          {country.name}
                        </option>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={4} xl={2}>
                  <FormControl className={globalStyles.formControl} fullWidth={true}>
                    <InputLabel shrink={true}>State</InputLabel>
                    <Select
                      native
                      labelId="state-code-label"
                      id="stateCode"
                      name="stateCode"
                      value={stateCode}
                      onChange={(e) => {
                        if (e !== null) {
                          setStateCode(e.target.value as string);
                        }
                        searchCriteriaChanged();
                      }}
                      label="Program"
                    >
                      <option value="">Any</option>
                      {states.map((state, index) => (
                        <option value={state.code} key={index}>
                          {state.name}
                        </option>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </>
            ) : null}

            <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
              <TextField
                required
                id="searchText"
                name="searchText"
                label="Search Text"
                value={searchText}
                onChange={(e) => {
                  if (e !== null) {
                    setSearchText(e.target.value as string);
                  }
                  searchCriteriaChanged();
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Box p={3} pt={3} display="flex" justifyContent="center">
                <Button type="submit" id="button-save" variant="contained" className={globalStyles.buttonDefault} disabled={loading} onClick={() => setShowAllFilterValues(!showAllFilterValues)}>
                  {showAllFilterValues ? "Less" : "More"}
                </Button>

                <Button type="submit" id="button-save" variant="contained" color="secondary" className={globalStyles.buttonDefault} disabled={loading} onClick={() => search()}>
                  {loading ? "Searching..." : "Search"}
                </Button>

                <Button id="button-cancel" color="default" className={globalStyles.buttonDefault} disabled={loading} onClick={() => searchFilterCriteriaReset()}>
                  Clear
                </Button>
              </Box>
            </Grid>
          </Grid>

          <Box className={showAllFilterValues ? pageStyles.datagridContainerMore : pageStyles.datagridContainerLess}>
            {loading ? <LinearProgress color="secondary" /> : null}
            <CustomDataGrid uniqueGridCode={"SubscriptionResult"} columns={columns} rows={getData()} showToolBar={true} sortModel={[{ field: "accountName", sort: "desc" }]} handleDataGridClick={handleDataGridClick} />
          </Box>
        </Box>
      </Screen>
    </React.Fragment>
  );
};

const LocalPageStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      padding: 0,
    },
    datagridContainerLess: {
      minHeight: "500px",
      height: "calc(100vh - 450px)",
      width: "100%",
      marginBottom: theme.spacing(2),
    },
    datagridContainerMore: {
      minHeight: "500px",
      height: "calc(100vh - 600px)",
      width: "100%",
      marginBottom: theme.spacing(2),
    },
  })
);

export default Subscription;
